import React, { useState } from 'react'
import { Icon, IconType } from '../Icon'
import { Loader, LoaderType } from '../Loader'
import { NotificationType } from '../Notification'
import { useMutation } from '@apollo/client'
import { DELETE_PORTFOLIO_ITEM } from '../../gql'
import { useNotification } from '../../utils/hooks'
import { DeleteModal } from '../DeleteModal'

interface Props {
  portfolioId: number
  portfolioItemsCount?: number
  minPortfolioItems?: number
}

export const DeletePortfolioItemButton: React.FC<Props> = ({
  portfolioId,
  portfolioItemsCount,
  minPortfolioItems,
}) => {
  const { addNotification } = useNotification()
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  const [deletePortfolioItem] = useMutation(DELETE_PORTFOLIO_ITEM)

  const handleDeleteClick = () => {
    if (
      portfolioItemsCount !== undefined &&
      minPortfolioItems !== undefined &&
      portfolioItemsCount <= minPortfolioItems
    ) {
      addNotification(
        `You must have at least ${minPortfolioItems} portfolio items. Upload more before deleting.`,
        NotificationType.error,
      )
      return
    }
    setIsConfirmOpen(true)
  }

  const deleteContent = async () => {
    try {
      setIsDeleting(true)

      await deletePortfolioItem({
        variables: {
          portfolioItemId: portfolioId,
        },
        update(cache) {
          const normalizedId = cache.identify({
            __typename: 'PortfolioItem',
            id: portfolioId,
          })

          cache.evict({ id: normalizedId })
          cache.gc()

          // Keep pagination.numberOfHits in sync so InfiniteScroll
          // correctly calculates whether more items remain to load.
          cache.modify({
            fields: {
              getPortfolioByCreator(existing) {
                if (!existing?.pagination) return existing
                return {
                  ...existing,
                  pagination: {
                    ...existing.pagination,
                    numberOfHits: Math.max(
                      0,
                      existing.pagination.numberOfHits - 1,
                    ),
                  },
                }
              },
            },
          })
        },
      })

      addNotification(`Portfolio item deleted.`, NotificationType.success)
      setIsDeleting(false)
    } catch (error) {
      addNotification(
        `Something went wrong. Please try again.`,
        NotificationType.error,
      )
      setIsDeleting(false)
    }
  }

  if (isDeleting) {
    return (
      <div className='flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-gray-100 p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200'>
        <Loader type={LoaderType.button} className='mr-0 !h-5 !w-5' />
      </div>
    )
  }

  return (
    <>
      <DeleteModal
        title='Confirm'
        description='Are you sure you want to delete this video from your portfolio? All of your data will be permanently removed. This action cannot be undone.'
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        onDelete={deleteContent}
      />
      <button
        type='button'
        onClick={handleDeleteClick}
        className='flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-gray-100 p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200'
      >
        <Icon type={IconType.trash} className='h-5 w-5 text-primary' />
      </button>
    </>
  )
}
